import type { ApiContext } from "./context";

type PendingAttempt = { attemptId: string; scanId?: string; createdAt: number };

function storageKey(ctx: ApiContext, shopId: string): string | null {
  if (!ctx.authToken) return null;
  try {
    const part = ctx.authToken.split(".")[1];
    const claims = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/"))) as { sub?: string; iss?: string };
    return claims.sub ? `scan-attempts:${claims.iss}:${claims.sub}:${shopId}` : null;
  } catch {
    return null;
  }
}

export function pendingAttempts(ctx: ApiContext, shopId: string): PendingAttempt[] {
  try {
    const key = storageKey(ctx, shopId);
    const value: unknown = key ? JSON.parse(localStorage.getItem(key) ?? "[]") : [];
    if (!Array.isArray(value)) return [];
    return value.filter(
      (a): a is PendingAttempt =>
        a &&
        typeof a.attemptId === "string" &&
        typeof a.createdAt === "number" &&
        a.createdAt > Date.now() - 12 * 60 * 60 * 1000 &&
        (a.scanId === undefined || typeof a.scanId === "string"),
    );
  } catch {
    return [];
  }
}

function save(ctx: ApiContext, shopId: string, attempts: PendingAttempt[]) {
  try {
    const key = storageKey(ctx, shopId);
    if (key) localStorage.setItem(key, JSON.stringify(attempts));
  } catch {
    /* Storage may be disabled; persisted scans remain available in history. */
  }
}

export function rememberAttempt(ctx: ApiContext, shopId: string, attemptId: string, scanId?: string) {
  const attempts = pendingAttempts(ctx, shopId);
  const previous = attempts.find((a) => a.attemptId === attemptId);
  save(ctx, shopId, [
    ...attempts.filter((a) => a.attemptId !== attemptId),
    { ...previous, attemptId, ...(scanId ? { scanId } : {}), createdAt: previous?.createdAt ?? Date.now() },
  ]);
}

export function forgetAttempt(ctx: ApiContext, shopId: string, attemptId: string) {
  save(
    ctx,
    shopId,
    pendingAttempts(ctx, shopId).filter((a) => a.attemptId !== attemptId),
  );
}
